<template>
  <bj-modal
    v-if="visible"
    v-model="visible"
    size="sm"
    title="Order Report"
  >
    <div slot="content">
      <div class="row gutter-sm">
        <q-field
          :error="get($v.payload, 'business_id.$error')"
          error-label="This field is required"
          class="col-8"
        >
          <autocomplete
            method="autocompleteBusiness"
            placeholder="Business..."
            @change="businessSelected"
            @clear="payload.business_id =null"
          />
        </q-field>
        <q-field
          :error="get($v.payload, 'type.$error')"
          error-label="This field is required"
          class="col-4"
        >
          <q-select
            v-model="payload.type"
            float-label="Report Type"
            :options="inspectionTypesOptions"
            @input="typeSelected"
          />
        </q-field>
        <q-field
          :error="get($v.payload,'property_id.$error')"
          error-label="This field is required"
          class="col-6"
        >
          <autocomplete
            method="autocompleteProperty"
            :disable="!payload.business_id"
            :filters="payload.business_id ? {'business_id':payload.business_id} : null"
            placeholder="Property..."
            @change="propertySelected"
          />
        </q-field>
        <q-field
          v-if="selectedProperty && get(selectedProperty,'units[1]')"
          :error="get($v.payload, 'unit_id.$error')"
          error-label="This field is required"
          class="col-6"
        >
          <select-property-unit
            :property-id="payload.property_id"
            :current-unit-id="payload.unit_id"
            @change="unitSelected"
          />
        </q-field>
        <q-field
          :error="get($v.payload,'associated_business_user_id.$error')"
          error-label="This field is required"
          class="col-6"
        >
          <autocomplete
            placeholder="Associated business member"
            method="autocompleteBusinessVerifiedUsers"
            :min-characters="0"
            :filters="payload.business_id ? {'business_id':payload.business_id} : null"
            :disable="!payload.business_id"
            :hits-per-page="100"
            @change="userSelected"
          />
        </q-field>
        <q-field
          v-if="requiredDatesArray.includes(this.payload.type)"
          :error="get($v.payload, 'preferred_from.$error')"
          error-label="This field is required"
          class="col-6"
        >
          <q-datetime
            v-model="payload.preferred_from"
            :type="isTimeRequired ? 'datetime' : 'date'"
            stack-label="Preferred Inspection at (in property timezone)"
            :format="isTimeRequired ? 'MM/DD/YY h:mmA' : 'MM/DD/YY'"
          />
        </q-field>
      </div>
      <bj-inner-loading
        label="Saving"
        :visible="loading"
      />
    </div>
    <div slot="footer">
      <q-btn-group
        rounded
        flat
      >
        <q-btn
          :disable="loading"
          rounded
          color="primary"
          icon="save"
          size="lg"
          outline
          label="Open Report Order"
          no-caps
          @click.prevent="add"
        />
      </q-btn-group>
    </div>
  </bj-modal>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import get from 'lodash.get';
import SelectPropertyUnit from '@/components/select-property-unit';
import Autocomplete from '@/components/autocomplete.vue';
import momentUtil from '../utils/MomentUtil';
import config from '../plugins/bluejay/config';

const defaultPayload = {
  type: null,
  business_id: null,
  property_id: null,
  unit_id: null,
  associated_business_user_id: null,
  preferred_from: null,
};

const inspectionTypesOptions = [
  { label: '', value: null },
  { label: 'Periodic', value: config.inspectionTypes.PERIODIC },
  { label: 'Move In/Out', value: config.inspectionTypes.MOVE_IN_OUT },
  { label: 'Vacancy Check', value: config.inspectionTypes.VACANCY_CHECK },
];

export default {
  components: {
    Autocomplete,
    SelectPropertyUnit,
  },
  validations() {
    return {
      payload: {
        business_id: { required },
        property_id: { required },
        unit_id: { required },
        associated_business_user_id: { required },
        type: { required },
        preferred_from: {
          required: requiredIf(function () {
            return [config.inspectionTypes.MOVE_IN_OUT, config.inspectionTypes.VACANCY_CHECK].includes(this.payload.type);
          }),
        },
      },
    };
  },
  data() {
    return {
      visible: false,
      loading: false,
      payload: this.clone(defaultPayload),
      selectedBusiness: null,
      selectedProperty: null,
      inspectionTypesOptions,
      config,
      requiredDatesArray: [config.inspectionTypes.MOVE_IN_OUT, config.inspectionTypes.VACANCY_CHECK],
    };
  },
  computed: {
    isTimeRequired() {
      return this.payload.type === config.inspectionTypes.MOVE_IN_OUT;
    },
  },
  methods: {
    get,
    show() {
      this.visible = true;
      this.loading = false;
      this.cleanModalState();
      this.$v.$reset();
    },
    clearField(...fields) {
      fields.forEach((field) => {
        this.payload[field] = null;
      });
    },
    businessSelected(event) {
      const { item, navigateWithKeyboardArrows } = event;
      if (!navigateWithKeyboardArrows) {
        this.selectedBusiness = item.data;
        this.payload.business_id = item.data.id;
      }
    },
    propertySelected(event) {
      const { item, navigateWithKeyboardArrows } = event;
      if (!navigateWithKeyboardArrows) {
        this.payload.property_id = item.data.id;
        this.selectedProperty = item.data;
        this.payload.unit_id = get(item, 'data.units', []).length === 1 ? item.data.units[0].id : null;
      }
    },
    unitSelected(unitId) {
      this.payload.unit_id = unitId;
    },
    userSelected(event) {
      const { item, navigatedWithKeyboard } = event;
      if (!navigatedWithKeyboard) {
        this.payload.associated_business_user_id = item.data.id;
      }
    },
    typeSelected(type) {
      if (type === config.inspectionTypes.PERIODIC) {
        this.payload.preferred_from = null;
      }
    },
    async add() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.loading = true;
      const payload = this.buildPayload();

      try {
        await this.storeInspection(payload);
      } catch (error) {
        if (error.error === 'existingInspection') {
          await this.confirmDuplicateInspection(error);
          return;
        }

        this.loading = false;
        this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative', 5000);
      }
    },
    buildPayload() {
      const payload = { ...this.payload };

      if (payload.preferred_from) {
        const inspectedAt = momentUtil.changeTz(payload.preferred_from, this.selectedProperty.timezone);
        payload.preferred_from = payload.type === config.inspectionTypes.VACANCY_CHECK ? inspectedAt.toDateFormat() : inspectedAt.tz('UTC').toServerFormat();
      } else {
        delete payload.preferred_from;
      }

      return payload;
    },
    storeInspection(payload) {
      return this.api().request('POST', 'admin/inspections', payload).then(() => {
        this.loading = false;
        this.visible = false;
        this.uiNotify('Inspection added successfully');
        this.cleanModalState();
        this.$emit('added');
      });
    },
    async confirmDuplicateInspection(error) {
      this.loading = false;

      try {
        const inspections = error.existingInspections || [error.existingInspection].filter(Boolean);
        await this.uiConfirm(
          this.getDuplicateInspectionMessage(inspections),
          'Existing Report',
          'Order Another Report',
          'Cancel',
        );
        this.loading = true;
        await this.storeInspection({ ...this.buildPayload(), allow_duplicate: true });
      } catch (confirmError) {
        if (confirmError && confirmError.message) {
          this.uiNotify(this.getFirstError(confirmError) || confirmError.message || 'Error occurred', 'negative', 5000);
        }
        this.loading = false;
      }
    },
    getDuplicateInspectionMessage(inspections) {
      const reportLines = inspections.map((inspection) => {
        const type = inspection.displayable_type || inspection.type || 'Condition report';
        const state = inspection.state || 'in progress';
        const orderedAt = inspection.ordered_at || 'unknown date';

        return `#${inspection.id} - ${type} - ${state} - ordered ${orderedAt}`;
      });

      return [
        'This unit already has an open condition report:',
        ...reportLines,
        'Do you want to order another report for this unit?',
      ].join('\n');
    },
    cleanModalState() {
      this.payload = this.clone(defaultPayload);
      this.selectedProperty = null;
    },
  },
};
</script>
